import React from "react";
import { graphql } from "gatsby";
// import Layout from "../components/layout"
// import SEO from "../components/seo"

import { getImage, GatsbyImage } from "gatsby-plugin-image";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { LinkWithUTM as Link } from "../components/LinkWithUTM";
import FacebookIcon from "../components/icons/Facebook";
import LinkedinIcon from "../components/icons/Linkedin";
import MailIcon from "../components/icons/Mail";
import TwitterIcon from "../components/icons/Twitter";
import BlogAuthorCard from "../components/BlogAuthorCard";
import AdviceArticleCarousel from "../components/AdviceArticleCarousel";

import "../../scss/main.scss";
import "../../scss/_blog.scss";

export default function Template(props) {
	const { data } = props;
	const {
		pageContext: { feature_image },
	} = props;

	const {
		ghostPost: {
			primary_author,
			primary_tag,
			title,
			excerpt,
			published_at,
			feature_image_sharp,
			slug,
			html_sections,
		},
		relatedPosts,
	} = data;

	const allNewsblockData = relatedPosts.nodes.filter(
		({ slug: relatedPostSlug }) => relatedPostSlug !== slug
	);
	const canonicalPath = `/blog/${slug}/`;
	const shareUrl = `https://www.edyn.care${canonicalPath}`;
	const shareText = `A practical guide from Edyn Care: ${title}`;
	const articleSchema = {
		"@context": "https://schema.org",
		"@type": "Article",
		headline: title,
		description: excerpt,
		image: feature_image,
		author: primary_author && {
			"@type": "Person",
			name: primary_author.name,
		},
		publisher: {
			"@type": "Organization",
			name: "Edyn Care",
		},
		mainEntityOfPage: `https://www.edyn.care${canonicalPath}`,
	};

	return (
		<>
			<Seo
				title={`${title} | Edyn Care`}
				description={
					excerpt ||
					"Practical guidance from Edyn Care for families planning and arranging live-in care at home."
				}
				path={canonicalPath}
				image={feature_image}
			>
				<script type="application/ld+json">
					{JSON.stringify(articleSchema)}
				</script>
			</Seo>
			<div className="container edyn-commercial-shell edyn-article-shell">
				<Header />
				<main id="main-content" className="blog-post blog-post--article">
					<div className="blog-post__above-the-fold blog-post__above-the-fold--article">
						<div className="blog-post__article-masthead">
							<Link className="blog-post__back" to="/blog/">
								Back to advice <span aria-hidden="true">&#8592;</span>
							</Link>
							<p className="eyebrow eyebrow--light">
								{primary_tag?.name || "Edyn advice"}
							</p>
							<h1 className="blog-post__above-the-fold-title">{title}</h1>
							<div className="blog-post__article-meta">
								<span>{published_at}</span>
								<span>Practical guidance from Edyn</span>
							</div>
						</div>
					</div>
					<GatsbyImage
						className="blog-post__hero-image"
						image={getImage(feature_image_sharp)}
						alt="Blog post image"
						objectFit="cover"
					/>
					<section className="blog-post-section">
						<div className="blog-post__content">
							{html_sections.json
								.filter(({ tagName }) => tagName !== null)
								.map(({ tagName, innerHtml, attributes }) => {
									if (
										attributes &&
										attributes.data_edyn_component === "primary-button"
									) {
										if (attributes.data_edyn_url.startsWith("tel:"))
											return (
												<div className="blog-button-wrapper">
													<a
														href={attributes.data_edyn_url}
														className="btn-primary"
													>
														{innerHtml}
													</a>
												</div>
											);
										return (
											<div className="blog-button-wrapper">
												<Link
													to={attributes.data_edyn_url}
													className="btn-primary"
												>
													{innerHtml}
												</Link>
											</div>
										);
									}
									const HtmlTag = tagName;
									if (tagName.toLowerCase() === "hr") return <hr />;
									return (
										<HtmlTag
											id={attributes.id}
											className={attributes.class}
											dangerouslySetInnerHTML={{ __html: innerHtml }}
										/>
									);
								})}
						</div>

						<div className="blog-post__socials" aria-label="Share this guide">
							<p>Share this guide</p>
							<a
								href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
									shareUrl
								)}`}
								target="_blank"
								rel="noreferrer"
								aria-label="Share on Facebook"
							>
								<FacebookIcon className="social-icon" />
							</a>
							<a
								href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
									shareText
								)}&url=${encodeURIComponent(shareUrl)}`}
								target="_blank"
								rel="noreferrer"
								aria-label="Share on X"
							>
								<TwitterIcon className="social-icon" />
							</a>
							<a
								href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
									shareUrl
								)}`}
								target="_blank"
								rel="noreferrer"
								aria-label="Share on LinkedIn"
							>
								<LinkedinIcon className="social-icon" />
							</a>
							<a
								href={`mailto:?subject=${encodeURIComponent(
									title
								)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`}
								aria-label="Share by email"
							>
								<MailIcon className="social-icon" />
							</a>
						</div>

						<BlogAuthorCard
							authorTitle={primary_author.bio}
							authorName={primary_author.name}
							authorProfileImage={getImage(primary_author.profile_image_sharp)}
						/>
					</section>
					{allNewsblockData && allNewsblockData.length > 0 && (
						<section className="article-related" aria-labelledby="related-posts-title">
							<div className="article-related__inner">
								<div className="article-related__heading">
									<p className="eyebrow">Keep reading</p>
									<h2 id="related-posts-title">Related posts</h2>
								</div>
								<AdviceArticleCarousel posts={allNewsblockData} />
							</div>
						</section>
					)}
				</main>
				<Footer />
			</div>
		</>
	);
}

export const pageQuery = graphql`
	query($slug: String!, $tagSlug: String!) {
		ghostPost(slug: { eq: $slug }) {
			html
			html_sections {
				json {
					tagName
					innerHtml
					attributes {
						data_edyn_component
						data_edyn_url
						class
						id
					}
				}
			}
			title
			primary_tag {
				name
			}
			slug
			published_at(formatString: "DD MMM YYYY")
			feature_image_sharp {
				childImageSharp {
					gatsbyImageData
				}
			}
			primary_author {
				bio
				name
				profile_image_sharp {
					childImageSharp {
						gatsbyImageData
					}
				}
			}
		}
		relatedPosts: allGhostPost(
			filter: { primary_tag: { slug: { eq: $tagSlug } } }
		) {
			nodes {
				title
				slug
				feature_image_sharp {
					childImageSharp {
						gatsbyImageData
					}
				}
				excerpt
				published_at(formatString: "DD MMM YYYY")
			}
		}
	}
`;
